import { useState, useEffect } from "react";

const AUTH_API_BASE =
  import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

interface FeatureFlag {
  flag_key: string;
  enabled: boolean;
  description: string;
  updated_at: string;
}

async function adminFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const authData = localStorage.getItem("tradecraft-auth");
  const token = authData ? JSON.parse(authData).state?.token : null;

  const response = await fetch(`${AUTH_API_BASE}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error((data as { error: string }).error || "Request failed");
  }
  return data as T;
}

export function FeatureFlagsPanel() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);

  useEffect(() => {
    if (!expanded) return;
    loadFlags();
  }, [expanded]);

  const loadFlags = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch<FeatureFlag[]>("/api/v1/admin/feature-flags");
      setFlags(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (flag: FeatureFlag) => {
    setError(null);
    try {
      await adminFetch<FeatureFlag>(`/api/v1/admin/feature-flags/${flag.flag_key}`, {
        method: "PUT",
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      setSuccess(`${flag.flag_key} ${!flag.enabled ? "enabled" : "disabled"}`);
      await loadFlags();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update flag");
    }
  };

  return (
    <div className="rounded-xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-violet-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-text-primary">Feature Flags</div>
            <div className="text-xs text-text-secondary">
              Toggle features on/off without redeploying
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-text-secondary transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-surface-700/50">
          {loading && (
            <div className="text-center py-4 text-text-secondary text-sm">Loading...</div>
          )}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
              {success}
            </div>
          )}

          <p className="text-xs text-text-secondary pt-2">
            Changes take effect immediately for new page loads. Users may need to refresh.
          </p>

          {flags.map((flag) => (
            <div
              key={flag.flag_key}
              className="flex items-center justify-between p-3 rounded-lg bg-surface-900/50 border border-surface-700/50"
            >
              <div>
                <div className="text-sm font-medium text-text-primary font-mono">
                  {flag.flag_key}
                </div>
                <div className="text-xs text-text-secondary mt-0.5">{flag.description}</div>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(flag)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  flag.enabled ? "bg-cyan-500" : "bg-surface-600"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    flag.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
